import api from "../api";
import { getCurrentMonthDateRange } from "../../utils/dateRange";
import { parseAssetNodeId } from "../../components/CustomAssetTree/useCustomAssetTree";
import {
  DashboardFilter,
  DashboardRequestPayload,
  FinancialEvolution,
  ExpenseCategory,
  RevenueCategory,
  KPIData,
  DREHierarchicalResponse,
  DREExpandRequest,
  DREExpandResponse,
  DRENodeType,
  UploadFileResponse,
  SidebarTreeResponse,
} from "../../types/dashboard";

const buildRequestPayload = (filters?: DashboardFilter): DashboardRequestPayload => {
  const { startDate: defaultStartDate, endDate: defaultEndDate } = getCurrentMonthDateRange();

  const payload: DashboardRequestPayload = {
    accountId: "",
    startDate: filters?.startDate || defaultStartDate,
    endDate: filters?.endDate || defaultEndDate,
  };

  if (filters?.categoryIds) payload.categoryIds = filters.categoryIds.join(",");
  if (filters?.clientIds) payload.clientIds = filters.clientIds.join(",");
  if (filters?.vehicleIds) payload.vehicleIds = filters.vehicleIds.join(",");
  if (filters?.contracts) payload.contracts = filters.contracts.join(",");

  return payload;
};

/**
 * Build payload for POST requests (JSON body).
 * Arrays are sent as actual arrays, not comma-separated strings.
 * This is required for DRE expand endpoints.
 */
interface PostRequestPayload {
  startDate: string;
  endDate: string;
  regionalIds?: number[];
  categoryIds?: number[];
  clientIds?: number[];
  vehicleIds?: number[];
  contracts?: string[];
}

const buildPostRequestPayload = (filters?: DashboardFilter): PostRequestPayload => {
  const { startDate: defaultStartDate, endDate: defaultEndDate } = getCurrentMonthDateRange();

  const payload: PostRequestPayload = {
    startDate: filters?.startDate || defaultStartDate,
    endDate: filters?.endDate || defaultEndDate,
  };

  const selectedRegionalIds = new Set<string>();

  // Convert string arrays to number arrays for POST body
  if (filters?.regional?.length) {
    payload.regionalIds = filters.regional
      .map((id) => {
        const parsed = parseAssetNodeId(id);
        if (parsed.regionalId) selectedRegionalIds.add(parsed.regionalId);
        return parseInt(parsed.regionalId, 10);
      })
      .filter((id) => !isNaN(id));
  }

  if (filters?.categoryIds?.length) {
    payload.categoryIds = filters.categoryIds.map((id) => parseInt(id, 10)).filter((id) => !isNaN(id));
  }

  if (filters?.clientIds?.length) {
    payload.clientIds = filters.clientIds
      .map((id) => parseAssetNodeId(id))
      .filter((parsed) => !selectedRegionalIds.has(parsed.regionalId))
      .map((parsed) => parseInt(parsed.clientId, 10))
      .filter((id) => !isNaN(id));
  }

  if (filters?.vehicleIds?.length) {
    payload.vehicleIds = filters.vehicleIds
      .map((id) => parseAssetNodeId(id))
      .filter((parsed) => !selectedRegionalIds.has(parsed.regionalId))
      .map((parsed) => parseInt(parsed.vehicleId, 10))
      .filter((id) => !isNaN(id));
  }

  if (filters?.contracts?.length) {
    payload.contracts = filters.contracts
      .map((id) => parseAssetNodeId(id))
      .filter((parsed) => !selectedRegionalIds.has(parsed.regionalId))
      .map((parsed) => parsed.contract)
      .filter(Boolean);
  }

  return payload;
};

export const dashboardService = {
  getKPI: async (filters?: DashboardFilter): Promise<KPIData> => {
    try {
      // Use POST with JSON body - arrays must be sent as arrays, not comma-separated strings
      const payload = buildPostRequestPayload(filters);
      const response = await api.post("/dre/summary", payload);
      return response.data.data;
    } catch (error) {
      console.error("Error fetching KPI:", error);
      throw error;
    }
  },
  getVolksTotalDRE: async (filters?: DashboardFilter): Promise<DREHierarchicalResponse> => {
    try {
      // Use POST with JSON body - arrays must be sent as arrays, not comma-separated strings
      const payload = buildPostRequestPayload(filters);
      const response = await api.post("/dre/complete", payload);
      return response.data.data;
    } catch (error) {
      console.error("Error fetching DRE:", error);
      throw error;
    }
  },
  getVolksTotalDREEvolution: async (filters?: DashboardFilter): Promise<DREHierarchicalResponse> => {
    try {
      // Use POST with JSON body - arrays must be sent as arrays, not comma-separated strings
      const payload = buildPostRequestPayload(filters);
      const response = await api.post("/dre/complete/evolution", payload);
      return response.data.data;
    } catch (error) {
      console.error("Error fetching DRE evolution:", error);
      throw error;
    }
  },
  /**
   * Upload de arquivo Excel com processamento assíncrono.
   * Retorna imediatamente com status PROCESSING e faz polling até completar.
   */
  uploadExpenseFile: async (
    file?: File | null,
    onProgress?: (status: string, processedRows: number, totalRows: number) => void
  ): Promise<UploadFileResponse | null> => {
    try {
      if (!file) return null;

      const formData = new FormData();
      formData.append("file", file);

      // 1. Envia o arquivo (retorna 202 Accepted imediatamente)
      const uploadResponse = await api.post("/uploads", formData);
      const uploadId = uploadResponse.data?.data?.id;

      if (!uploadId) {
        console.error("Upload failed: no upload ID returned");
        return null;
      }

      // 2. Polling até o upload completar
      const pollInterval = 2000; // 2 segundos
      const maxAttempts = 300; // 10 minutos máximo
      let attempts = 0;

      while (attempts < maxAttempts) {
        const statusResponse = await api.get(`/uploads/${uploadId}`);
        const uploadData = statusResponse.data?.data;
        const status = uploadData?.status;

        // Notifica progresso
        if (onProgress && uploadData) {
          onProgress(status, uploadData.processedRows || 0, uploadData.totalRows || 0);
        }

        // Verifica se terminou
        if (status === "COMPLETED" || status === "PARTIAL" || status === "FAILED") {
          // Busca erros se houver
          let errors: any[] = [];
          if (uploadData.errorRows > 0 || status === "FAILED" || status === "PARTIAL") {
            try {
              const errorsResponse = await api.get(`/uploads/${uploadId}/errors?size=1000&page=0`);
              const content =
                errorsResponse.data?.data?.content || errorsResponse.data?.data || errorsResponse.data?.content;
              errors = Array.isArray(content) ? content : [];
            } catch (e) {
              console.warn("Could not fetch upload errors:", e);
            }
          }

          return {
            status: statusResponse.data.status,
            message: statusResponse.data.message,
            data: {
              upload: uploadData,
              errors: errors,
            },
            timestamp: statusResponse.data.timestamp,
          };
        }

        // Aguarda antes do próximo polling
        await new Promise((resolve) => setTimeout(resolve, pollInterval));
        attempts++;
      }

      // Timeout - retorna o último status conhecido
      console.error("Upload polling timeout");
      return null;
    } catch (error) {
      console.error("Error uploading expense file:", error);
      return null;
    }
  },
  getAssetTree: async (filters?: DashboardFilter): Promise<SidebarTreeResponse> => {
    try {
      const payload = buildRequestPayload(filters);
      const initDate = payload.startDate;
      const endDate = payload.endDate;
      const response = await api.get(`/sidebar/tree?startDate=${initDate}&endDate=${endDate}`);
      return response.data;
    } catch (error) {
      console.error("Error fetching asset tree:", error);
      throw error;
    }
  },
  getDRE: async (filters?: DashboardFilter): Promise<DREHierarchicalResponse> => {
    try {
      const payload = buildRequestPayload(filters);
      const response = await api.post("/dashboard/dre/hierarchical", payload);
      return response.data;
    } catch (error) {
      console.error("Error fetching DRE:", error);
      throw error;
    }
  },

  getFinancialEvolution: async (filters?: DashboardFilter): Promise<FinancialEvolution[]> => {
    try {
      const filter = buildRequestPayload(filters);

      const payload = {
        ...filter,
        ...(filter?.categoryIds ? { categoryIds: filter?.categoryIds?.split(",") } : {}),
        ...(filter?.clientIds ? { clientIds: filter?.clientIds?.split(",") } : {}),
        ...(filter?.contracts ? { contracts: filter?.contracts?.split(",") } : {}),
        ...(filter?.vehicleIds ? { vehicleIds: filter?.vehicleIds?.split(",") } : {}),
      };

      const response = await api.post("/dashboard/financial-evolution", payload);
      return response.data;
    } catch (error) {
      console.error("Error fetching financial evolution:", error);
      throw error;
    }
  },

  getExpenses: async (filters?: DashboardFilter): Promise<any> => {
    try {
      const payload = buildRequestPayload(filters);
      const response = await api.post("/dashboard/expenses", payload);
      return response.data;
    } catch (error) {
      console.error("Error fetching expenses:", error);
      throw error;
    }
  },

  getRevenues: async (filters?: DashboardFilter): Promise<any> => {
    try {
      const payload = buildRequestPayload(filters);
      const response = await api.post("/dashboard/revenues", payload);
      return response.data;
    } catch (error) {
      console.error("Error fetching revenues:", error);
      throw error;
    }
  },

  getExpenseCategories: async (filters?: DashboardFilter): Promise<ExpenseCategory[]> => {
    try {
      const filter = buildRequestPayload(filters);

      const payload = {
        ...filter,
        ...(filter?.categoryIds ? { categoryIds: filter?.categoryIds?.split(",") } : {}),
        ...(filter?.clientIds ? { clientIds: filter?.clientIds?.split(",") } : {}),
        ...(filter?.contracts ? { contracts: filter?.contracts?.split(",") } : {}),
        ...(filter?.vehicleIds ? { vehicleIds: filter?.vehicleIds?.split(",") } : {}),
      };

      const response = await api.post("/dashboard/expense/categories", payload);
      return response.data;
    } catch (error) {
      console.error("Error fetching expense categories:", error);
      throw error;
    }
  },

  getRevenueCategories: async (filters?: DashboardFilter): Promise<RevenueCategory[]> => {
    try {
      const filter = buildRequestPayload(filters);

      const payload = {
        ...filter,
        ...(filter?.categoryIds ? { categoryIds: filter?.categoryIds?.split(",") } : {}),
        ...(filter?.clientIds ? { clientIds: filter?.clientIds?.split(",") } : {}),
        ...(filter?.contracts ? { contracts: filter?.contracts?.split(",") } : {}),
        ...(filter?.vehicleIds ? { vehicleIds: filter?.vehicleIds?.split(",") } : {}),
      };

      const response = await api.post("/dashboard/revenue/categories", payload);
      return response.data;
    } catch (error) {
      console.error("Error fetching revenue categories:", error);
      throw error;
    }
  },

  /**
   * Lazy loading for DRE hierarchy.
   * Sprint 19 - US-18464: New hierarchy with 6 levels:
   * - CATEGORIA (level 1) → /expand/category/{id}/subcategories
   * - SUBCATEGORIA (level 2) → /expand/category/{catId}/subcategory/{subId}/segments
   * - REGIONAL (level 3) → /expand/category/{catId}/subcategory/{subId}/segment/{segId}/clients
   * - CLIENTE (level 4) → /expand/category/{catId}/client/{clientId}/contracts
   * - VIAGEM (level 5) → /expand/category/{catId}/client/{clientId}/contract/{contract}/vehicles
   * - VEICULO (level 6) → leaf node
   */
  expandDRENode: async (filters: DashboardFilter, expandRequest: DREExpandRequest): Promise<DREExpandResponse> => {
    try {
      // Use POST payload with arrays (not comma-separated strings) for DRE expand endpoints
      const payload = buildPostRequestPayload(filters);
      const { nodeId, nodeType, lineType, parentPath, getEvolutionValues } = expandRequest;

      // Determine the backend type (RECEITA or DESPESA) from lineType
      const backendType = lineType === "RECEITA_BRUTA" || lineType === "RECEITA_LIQUIDA" ? "RECEITA" : "DESPESA";

      // Helper to convert null/undefined IDs to "0" for backend (0 = null marker)
      const toPathId = (id: string | number | null | undefined): string => {
        if (id === null || id === undefined || id === "null" || id === "") return "0";
        return String(id);
      };

      let endpoint = "";
      let response;
      let hierarchyPathKey = "";

      switch (nodeType) {
        case "CATEGORIA": {
          // Sprint 19: Expanding a category → load subcategories (NEW)
          // FIX: Added /dre prefix to match backend @RequestMapping("/api/v1/dre")
          endpoint = `/dre/expand/category/${toPathId(nodeId)}/subcategories${getEvolutionValues ? "/evolution" : ""}?type=${backendType}`;
          response = await api.post(endpoint, payload);
          hierarchyPathKey = toPathId(nodeId);
          break;
        }
        case "SUBCATEGORIA": {
          // Sprint 19: Expanding a subcategory → load segments/regionals (NEW)
          const categoryId = toPathId(parentPath?.categoryId);
          const subcatId = toPathId(nodeId); // nodeId is the subcategory ID (0 = "Sem Subcategoria")
          endpoint = `/dre/expand/category/${categoryId}/subcategory/${subcatId}/segments${getEvolutionValues ? "/evolution" : ""}?type=${backendType}`;
          response = await api.post(endpoint, payload);
          hierarchyPathKey = categoryId + "_" + subcatId;
          break;
        }
        case "REGIONAL":
        case "SEGMENTO": {
          // Sprint 19: Expanding a regional/segment → load clients (NEW)
          const categoryId = toPathId(parentPath?.categoryId);
          const subcategoryId = toPathId(parentPath?.subcategoryId);
          const segmentId = toPathId(nodeId); // nodeId is the segment ID (0 = "Sem Regional")
          endpoint = `/dre/expand/category/${categoryId}/subcategory/${subcategoryId}/segment/${segmentId}/clients${getEvolutionValues ? "/evolution" : ""}?type=${backendType}`;
          response = await api.post(endpoint, payload);
          hierarchyPathKey = categoryId + "_" + subcategoryId + "_" + segmentId;
          break;
        }
        case "CLIENTE":
        case "FORNECEDOR": {
          // Expanding a client → load contracts
          const categoryId = toPathId(parentPath?.categoryId);
          const subcategoryId = toPathId(parentPath?.subcategoryId);
          const segmentId = toPathId(parentPath?.segmentId);
          const clientId = toPathId(nodeId);
          endpoint = `/dre/expand/category/${categoryId}/subcategory/${subcategoryId}/segment/${segmentId}/client/${clientId}/contracts${getEvolutionValues ? "/evolution" : ""}?type=${backendType}`;
          response = await api.post(endpoint, payload);
          hierarchyPathKey = categoryId + "_" + subcategoryId + "_" + segmentId + "_" + clientId;
          break;
        }
        case "VIAGEM":
        case "CONTRATO": {
          // Expanding a contract → load vehicles
          // Backend returns "CONTRATO" nodeType, frontend historically used "VIAGEM"
          const categoryId = toPathId(parentPath?.categoryId);
          const subcategoryId = toPathId(parentPath?.subcategoryId);
          const segmentId = toPathId(parentPath?.segmentId);
          const clientId = toPathId(parentPath?.clientOrSupplierId);
          // nodeId is the contract string
          endpoint = `/dre/expand/category/${categoryId}/subcategory/${subcategoryId}/segment/${segmentId}/client/${clientId}/contract/${encodeURIComponent(nodeId === "Sem Contrato" ? "0" : nodeId)}/vehicles${getEvolutionValues ? "/evolution" : ""}?type=${backendType}`;
          response = await api.post(endpoint, payload);
          hierarchyPathKey =
            categoryId +
            "_" +
            subcategoryId +
            "_" +
            segmentId +
            "_" +
            clientId +
            "_" +
            encodeURIComponent(nodeId === "Sem Contrato" ? "0" : nodeId);
          break;
        }
        default: {
          // VEICULO is a leaf node, should not expand
          console.warn("Attempting to expand leaf node:", nodeType);
          return { nodeId, children: [] };
        }
      }

      // Transform backend response to frontend format
      const children = response.data?.data || [];
      return {
        nodeId,
        children: children.map((item: any) => ({
          id: item.id === null || item.id === undefined ? "0" : String(item.id),
          compositeId: `${hierarchyPathKey}_${item.id === null || item.id === undefined ? "0" : String(item.id)}`,
          nodeType: item.nodeType || getNodeTypeFromLevel(nodeType),
          label: item.label,
          value: item.value,
          formattedValue: item.formattedValue,
          evolutionValues: item.evolutionValues,
          totals: item.totals,
          level: item.level || getNextLevel(nodeType),
          expandable: item.expandable,
          expanded: false,
          children: undefined,
        })),
      };
    } catch (error) {
      console.error("Error expanding DRE node:", error);
      throw error;
    }
  },
};

/**
 * Sprint 19 - US-18464: Helper to determine next node type in hierarchy
 * Hierarchy: CATEGORIA → SUBCATEGORIA → REGIONAL → CLIENTE → VIAGEM → VEICULO
 */
function getNodeTypeFromLevel(currentType: DRENodeType): DRENodeType {
  switch (currentType) {
    case "CATEGORIA":
      return "SUBCATEGORIA";
    case "SUBCATEGORIA":
      return "REGIONAL";
    case "REGIONAL":
    case "SEGMENTO":
      return "CLIENTE";
    case "CLIENTE":
    case "FORNECEDOR":
      return "CONTRATO"; // Contract level (backend returns CONTRATO)
    case "VIAGEM":
    case "CONTRATO":
      return "VEICULO";
    default:
      return "VEICULO";
  }
}

/**
 * Sprint 19 - US-18464: Helper to determine next level depth
 * Level 1: Categoria
 * Level 2: Subcategoria (NEW)
 * Level 3: Regional/Segmento (NEW)
 * Level 4: Cliente
 * Level 5: Contrato (Viagem)
 * Level 6: Veículo
 */
function getNextLevel(currentType: DRENodeType): number {
  switch (currentType) {
    case "CATEGORIA":
      return 2; // Subcategories are at level 2
    case "SUBCATEGORIA":
      return 3; // Regionals are at level 3
    case "REGIONAL":
    case "SEGMENTO":
      return 4; // Clients are at level 4
    case "CLIENTE":
    case "FORNECEDOR":
      return 5; // Contracts are at level 5
    case "VIAGEM":
    case "CONTRATO":
      return 6; // Vehicles are at level 6
    default:
      return 6;
  }
}

export default dashboardService;
